/**
 * OWL2 XML Namespace Constants
 *
 * Standard namespace URIs and prefixes used in OWL2 XML serialization.
 */

// =============================================================================
// Namespace URIs
// =============================================================================

/**
 * OWL 2 namespace
 */
export const OWL_NAMESPACE = 'http://www.w3.org/2002/07/owl#';

/**
 * RDF namespace
 */
export const RDF_NAMESPACE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#';

/**
 * RDF Schema namespace
 */
export const RDFS_NAMESPACE = 'http://www.w3.org/2000/01/rdf-schema#';

/**
 * XML Schema Datatypes namespace
 */
export const XSD_NAMESPACE = 'http://www.w3.org/2001/XMLSchema#';

/**
 * XML namespace
 */
export const XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace';

// =============================================================================
// Standard Prefixes
// =============================================================================

/**
 * Default prefix mappings for OWL2 ontologies.
 */
export const DEFAULT_PREFIXES: Record<string, string> = {
    owl: OWL_NAMESPACE,
    rdf: RDF_NAMESPACE,
    rdfs: RDFS_NAMESPACE,
    xsd: XSD_NAMESPACE,
    xml: XML_NAMESPACE,
};

// =============================================================================
// OWL2 Built-in Entity IRIs
// =============================================================================

/**
 * OWL2 built-in classes
 */
export const OWL_CLASSES = {
    Thing: `${OWL_NAMESPACE}Thing`,
    Nothing: `${OWL_NAMESPACE}Nothing`,
} as const;

/**
 * OWL2 built-in object properties
 */
export const OWL_OBJECT_PROPERTIES = {
    topObjectProperty: `${OWL_NAMESPACE}topObjectProperty`,
    bottomObjectProperty: `${OWL_NAMESPACE}bottomObjectProperty`,
} as const;

/**
 * OWL2 built-in data properties
 */
export const OWL_DATA_PROPERTIES = {
    topDataProperty: `${OWL_NAMESPACE}topDataProperty`,
    bottomDataProperty: `${OWL_NAMESPACE}bottomDataProperty`,
} as const;

/**
 * OWL2 built-in annotation properties
 */
export const OWL_ANNOTATION_PROPERTIES = {
    deprecated: `${OWL_NAMESPACE}deprecated`,
    versionInfo: `${OWL_NAMESPACE}versionInfo`,
    priorVersion: `${OWL_NAMESPACE}priorVersion`,
    backwardCompatibleWith: `${OWL_NAMESPACE}backwardCompatibleWith`,
    incompatibleWith: `${OWL_NAMESPACE}incompatibleWith`,
} as const;

/**
 * RDFS built-in annotation properties commonly used with OWL2
 */
export const RDFS_ANNOTATION_PROPERTIES = {
    label: `${RDFS_NAMESPACE}label`,
    comment: `${RDFS_NAMESPACE}comment`,
    seeAlso: `${RDFS_NAMESPACE}seeAlso`,
    isDefinedBy: `${RDFS_NAMESPACE}isDefinedBy`,
} as const;

// =============================================================================
// XSD Built-in Datatypes
// =============================================================================

/**
 * Commonly used XSD datatypes
 */
export const XSD_DATATYPES = {
    // Primitive types
    string: `${XSD_NAMESPACE}string`,
    boolean: `${XSD_NAMESPACE}boolean`,
    decimal: `${XSD_NAMESPACE}decimal`,
    float: `${XSD_NAMESPACE}float`,
    double: `${XSD_NAMESPACE}double`,
    duration: `${XSD_NAMESPACE}duration`,
    dateTime: `${XSD_NAMESPACE}dateTime`,
    time: `${XSD_NAMESPACE}time`,
    date: `${XSD_NAMESPACE}date`,
    gYearMonth: `${XSD_NAMESPACE}gYearMonth`,
    gYear: `${XSD_NAMESPACE}gYear`,
    gMonthDay: `${XSD_NAMESPACE}gMonthDay`,
    gDay: `${XSD_NAMESPACE}gDay`,
    gMonth: `${XSD_NAMESPACE}gMonth`,
    hexBinary: `${XSD_NAMESPACE}hexBinary`,
    base64Binary: `${XSD_NAMESPACE}base64Binary`,
    anyURI: `${XSD_NAMESPACE}anyURI`,
    QName: `${XSD_NAMESPACE}QName`,
    NOTATION: `${XSD_NAMESPACE}NOTATION`,

    // Derived types
    normalizedString: `${XSD_NAMESPACE}normalizedString`,
    token: `${XSD_NAMESPACE}token`,
    language: `${XSD_NAMESPACE}language`,
    NMTOKEN: `${XSD_NAMESPACE}NMTOKEN`,
    Name: `${XSD_NAMESPACE}Name`,
    NCName: `${XSD_NAMESPACE}NCName`,

    // Numeric derived types
    integer: `${XSD_NAMESPACE}integer`,
    nonPositiveInteger: `${XSD_NAMESPACE}nonPositiveInteger`,
    negativeInteger: `${XSD_NAMESPACE}negativeInteger`,
    long: `${XSD_NAMESPACE}long`,
    int: `${XSD_NAMESPACE}int`,
    short: `${XSD_NAMESPACE}short`,
    byte: `${XSD_NAMESPACE}byte`,
    nonNegativeInteger: `${XSD_NAMESPACE}nonNegativeInteger`,
    unsignedLong: `${XSD_NAMESPACE}unsignedLong`,
    unsignedInt: `${XSD_NAMESPACE}unsignedInt`,
    unsignedShort: `${XSD_NAMESPACE}unsignedShort`,
    unsignedByte: `${XSD_NAMESPACE}unsignedByte`,
    positiveInteger: `${XSD_NAMESPACE}positiveInteger`,
} as const;

/**
 * RDF built-in datatypes
 */
export const RDF_DATATYPES = {
    PlainLiteral: `${RDF_NAMESPACE}PlainLiteral`,
    XMLLiteral: `${RDF_NAMESPACE}XMLLiteral`,
    langString: `${RDF_NAMESPACE}langString`,
} as const;

/**
 * OWL2 built-in datatypes
 */
export const OWL_DATATYPES = {
    real: `${OWL_NAMESPACE}real`,
    rational: `${OWL_NAMESPACE}rational`,
} as const;

// =============================================================================
// XSD Facet URIs (for DatatypeRestriction)
// =============================================================================

/**
 * XSD constraining facets used in DatatypeRestriction
 */
export const XSD_FACETS = {
    length: `${XSD_NAMESPACE}length`,
    minLength: `${XSD_NAMESPACE}minLength`,
    maxLength: `${XSD_NAMESPACE}maxLength`,
    pattern: `${XSD_NAMESPACE}pattern`,
    enumeration: `${XSD_NAMESPACE}enumeration`,
    whiteSpace: `${XSD_NAMESPACE}whiteSpace`,
    maxInclusive: `${XSD_NAMESPACE}maxInclusive`,
    maxExclusive: `${XSD_NAMESPACE}maxExclusive`,
    minExclusive: `${XSD_NAMESPACE}minExclusive`,
    minInclusive: `${XSD_NAMESPACE}minInclusive`,
    totalDigits: `${XSD_NAMESPACE}totalDigits`,
    fractionDigits: `${XSD_NAMESPACE}fractionDigits`,
} as const;

// =============================================================================
// OWL2 XML Element Names
// =============================================================================

/**
 * OWL2 XML element names for class expressions
 */
export const CLASS_EXPRESSION_ELEMENTS = {
    Class: 'Class',
    ObjectIntersectionOf: 'ObjectIntersectionOf',
    ObjectUnionOf: 'ObjectUnionOf',
    ObjectComplementOf: 'ObjectComplementOf',
    ObjectOneOf: 'ObjectOneOf',
    ObjectSomeValuesFrom: 'ObjectSomeValuesFrom',
    ObjectAllValuesFrom: 'ObjectAllValuesFrom',
    ObjectHasValue: 'ObjectHasValue',
    ObjectHasSelf: 'ObjectHasSelf',
    ObjectMinCardinality: 'ObjectMinCardinality',
    ObjectMaxCardinality: 'ObjectMaxCardinality',
    ObjectExactCardinality: 'ObjectExactCardinality',
    DataSomeValuesFrom: 'DataSomeValuesFrom',
    DataAllValuesFrom: 'DataAllValuesFrom',
    DataHasValue: 'DataHasValue',
    DataMinCardinality: 'DataMinCardinality',
    DataMaxCardinality: 'DataMaxCardinality',
    DataExactCardinality: 'DataExactCardinality',
} as const;

/**
 * OWL2 XML element names for axioms
 */
export const AXIOM_ELEMENTS = {
    // Declarations
    Declaration: 'Declaration',

    // Class axioms
    SubClassOf: 'SubClassOf',
    EquivalentClasses: 'EquivalentClasses',
    DisjointClasses: 'DisjointClasses',
    DisjointUnion: 'DisjointUnion',

    // Object property axioms
    SubObjectPropertyOf: 'SubObjectPropertyOf',
    EquivalentObjectProperties: 'EquivalentObjectProperties',
    DisjointObjectProperties: 'DisjointObjectProperties',
    InverseObjectProperties: 'InverseObjectProperties',
    ObjectPropertyDomain: 'ObjectPropertyDomain',
    ObjectPropertyRange: 'ObjectPropertyRange',
    FunctionalObjectProperty: 'FunctionalObjectProperty',
    InverseFunctionalObjectProperty: 'InverseFunctionalObjectProperty',
    ReflexiveObjectProperty: 'ReflexiveObjectProperty',
    IrreflexiveObjectProperty: 'IrreflexiveObjectProperty',
    SymmetricObjectProperty: 'SymmetricObjectProperty',
    AsymmetricObjectProperty: 'AsymmetricObjectProperty',
    TransitiveObjectProperty: 'TransitiveObjectProperty',

    // Data property axioms
    SubDataPropertyOf: 'SubDataPropertyOf',
    EquivalentDataProperties: 'EquivalentDataProperties',
    DisjointDataProperties: 'DisjointDataProperties',
    DataPropertyDomain: 'DataPropertyDomain',
    DataPropertyRange: 'DataPropertyRange',
    FunctionalDataProperty: 'FunctionalDataProperty',

    // Datatype definition
    DatatypeDefinition: 'DatatypeDefinition',

    // Keys
    HasKey: 'HasKey',

    // Assertions
    SameIndividual: 'SameIndividual',
    DifferentIndividuals: 'DifferentIndividuals',
    ClassAssertion: 'ClassAssertion',
    ObjectPropertyAssertion: 'ObjectPropertyAssertion',
    NegativeObjectPropertyAssertion: 'NegativeObjectPropertyAssertion',
    DataPropertyAssertion: 'DataPropertyAssertion',
    NegativeDataPropertyAssertion: 'NegativeDataPropertyAssertion',

    // Annotation axioms
    AnnotationAssertion: 'AnnotationAssertion',
    SubAnnotationPropertyOf: 'SubAnnotationPropertyOf',
    AnnotationPropertyDomain: 'AnnotationPropertyDomain',
    AnnotationPropertyRange: 'AnnotationPropertyRange',
} as const;

/**
 * OWL2 XML element names for entities
 */
export const ENTITY_ELEMENTS = {
    Class: 'Class',
    Datatype: 'Datatype',
    ObjectProperty: 'ObjectProperty',
    DataProperty: 'DataProperty',
    AnnotationProperty: 'AnnotationProperty',
    NamedIndividual: 'NamedIndividual',
} as const;

/**
 * OWL2 XML attribute names
 */
export const XML_ATTRIBUTES = {
    IRI: 'IRI',
    abbreviatedIRI: 'abbreviatedIRI',
    cardinality: 'cardinality',
    nodeID: 'nodeID',
    datatypeIRI: 'datatypeIRI',
    ontologyIRI: 'ontologyIRI',
    versionIRI: 'versionIRI',
    'xml:lang': 'xml:lang',
} as const;
